import { useEffect, useState, lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { frame, cancelFrame } from 'framer-motion'
import Lenis from 'lenis'
import CustomCursor from './components/CustomCursor'
import TopBar from './components/TopBar'
import Footer from './components/Footer'
import BgSwitch from './components/BgSwitch'
import Home from './Home'
import DepartmentPage from './components/DepartmentPage'
import Solutions from './components/Solutions'
import ArticleIndex from './components/ArticleIndex'
import Article from './components/Article'
import LegalPage from './components/LegalPage'
import { PRIVACY, TERMS, IMPRINT } from './legal/content'

// Demo-urile = chunk-uri separate (lazy), încărcate doar pe /demos/* → bundle de marketing zvelt.
const CloseCockpit = lazy(() => import('./components/demos/close-cockpit/CloseCockpit').then((m) => ({ default: m.CloseCockpit })))
const ExcelToApp = lazy(() => import('./components/demos/excel-to-app/ExcelToApp').then((m) => ({ default: m.ExcelToApp })))
const DistributionCockpit = lazy(() => import('./components/demos/distribution-cockpit/DistributionCockpit').then((m) => ({ default: m.DistributionCockpit })))
import { DEPARTMENT_SLUGS } from './data/departments'
import { initCalEmbed } from './cal'
import { hasConsent, CONSENT_EVENT } from './consent'
import { LOCALES, getLocale, localePath } from './i18n'
import { LocaleProvider } from './i18n/locale.jsx'
import ConsentBanner from './components/ConsentBanner'

// Definițiile de rute (cale logică, fără prefix de limbă). Se înregistrează apoi pentru
// fiecare limbă: en la root, ro la /ro/*, de la /de/*.
const ROUTE_DEFS = [
  { path: '/', element: <Home /> },
  { path: '/solutions', element: <Solutions /> },
  { path: '/articole', element: <ArticleIndex /> },
  { path: '/articole/:slug', element: <Article /> },
  { path: '/privacy', element: <LegalPage content={PRIVACY} titleKey="privacyTitle" /> },
  { path: '/terms', element: <LegalPage content={TERMS} titleKey="termsTitle" /> },
  { path: '/imprint', element: <LegalPage content={IMPRINT} titleKey="imprintTitle" /> },
  ...DEPARTMENT_SLUGS.map((slug) => ({ path: `/${slug}`, element: <DepartmentPage slug={slug} /> })),
]

export default function App() {
  const [theme, setTheme] = useState('dark')
  const location = useLocation()
  const locale = getLocale(location.pathname)
  // Demo-urile (/demos/*) sunt aplicații full-screen cu propriul chrome → fără TopBar/Footer/
  // cursor și fără Lenis (care ar fura scroll-ul din tabele/drawere).
  const isDemo = location.pathname.startsWith('/demos')

  // Cal.com embed — inițializat în idle (off critical render path), dar gata înainte
  // ca utilizatorul să apese vreun CTA → popup-ul branded funcționează la primul tap.
  useEffect(() => {
    // La prerender (Playwright setează navigator.webdriver) NU încărca Cal: scroll-ul de
    // prerender ar declanșa warming-ul, iar runtime-ul Cal (custom elements) s-ar „bake" în
    // HTML și ar intra în conflict la reîncărcare. Userii reali nu au webdriver.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return

    const EVENTS = ['pointerdown', 'touchstart', 'keydown', 'mousemove', 'scroll']
    let done = false
    const warm = () => {
      if (done) return
      done = true
      disarm()
      initCalEmbed() // GDPR: doar cu consimțământ (vezi mai jos)
    }
    const disarm = () => EVENTS.forEach((e) => window.removeEventListener(e, warm))
    const arm = () => EVENTS.forEach((e) => window.addEventListener(e, warm, { passive: true }))

    // „Warm-on-intent" DOAR cu consimțământ: Cal (terț, setează cookie-uri) se inițializează
    // la prima intenție (mișcare/touch/scroll/tastă), dar numai dacă utilizatorul a acceptat.
    // Fără consimțământ, CTA-urile cad pe href (deschid pagina de booking în tab nou).
    if (hasConsent()) arm()

    // Dacă utilizatorul acceptă din banner, click-ul lui e deja „intenție" → warm imediat.
    const onConsent = (ev) => {
      if (ev.detail === 'granted') warm()
    }
    window.addEventListener(CONSENT_EVENT, onConsent)

    return () => {
      disarm()
      window.removeEventListener(CONSENT_EVENT, onConsent)
    }
  }, [])

  // Tema (dark/light) la marginea de sus — route-agnostică: citește data-theme de pe
  // secțiunea curentă. O consumă fundalul (BgSwitch) și header-ul (TopBar).
  useEffect(() => {
    let raf = 0
    const update = () => {
      // Asimetric: secțiunile LIGHT comută mai devreme (prag 0.22 — fix când intră,
      // pe golul de padding, înainte ca rândurile să animeze). Cele DARK comută târziu
      // (0.08) ca textul secțiunii care pleacă (ex. Contact) să nu cadă pe fundal greșit.
      const vh = window.innerHeight
      let t = 'dark'
      document.querySelectorAll('[data-theme]').forEach((s) => {
        const line = vh * (s.dataset.theme === 'light' ? 0.95 : 0.08)
        if (s.getBoundingClientRect().top <= line) t = s.dataset.theme
      })
      setTheme(t)
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; update() })
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [location.pathname])

  // Scroll la top la schimbarea rutei.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Smooth scroll (Lenis), condus din bucla de frame a framer-motion. NU pe demo-uri
  // (ar fura scroll-ul nativ din tabele/drawere).
  useEffect(() => {
    if (isDemo) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) return
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1 })
    const update = (data) => lenis.raf(data.timestamp)
    frame.update(update, true)
    return () => {
      cancelFrame(update)
      lenis.destroy()
    }
  }, [isDemo])

  return (
    <LocaleProvider locale={locale}>
      <BgSwitch theme={theme} />
      {!isDemo && <CustomCursor />}
      {!isDemo && <TopBar theme={theme} />}
      <main>
        <Routes>
          {/* Demo-uri interactive — full-screen, client-only, lazy (nu sunt prerendate) */}
          <Route
            path="/demos/close-cockpit"
            element={<Suspense fallback={<div style={{ minHeight: '100vh', background: '#1A231E' }} />}><CloseCockpit /></Suspense>}
          />
          <Route
            path="/demos/excel-to-app"
            element={<Suspense fallback={<div style={{ minHeight: '100vh', background: '#1A231E' }} />}><ExcelToApp /></Suspense>}
          />
          <Route
            path="/demos/distribution-cockpit"
            element={<Suspense fallback={<div style={{ minHeight: '100vh', background: '#F3F5F9' }} />}><DistributionCockpit /></Suspense>}
          />
          {/* Site de marketing — toate rutele × 3 limbi */}
          {LOCALES.flatMap((loc) =>
            ROUTE_DEFS.map((rd) => (
              <Route key={loc + rd.path} path={localePath(loc, rd.path)} element={rd.element} />
            )),
          )}
        </Routes>
      </main>
      {!isDemo && <Footer />}
      <ConsentBanner />
    </LocaleProvider>
  )
}
